import { Form, Button, Input, Select, Spin, DatePicker, AutoComplete, Card, Typography, Divider, Row, Col } from 'antd';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import React, { useCallback, useState } from 'react';
import dayjs from 'dayjs';

import { MdOutlineKeyboardArrowLeft } from 'react-icons/md';
import { FaUserGraduate } from 'react-icons/fa';

import '../../../css/UserAdmin/Global.css';
import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

const { Option } = Select;
const { Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL || '';

const CreateManualPayment = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    
    // Component State
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Search State
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchOptions, setSearchOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    // Form State
    const [paymentType, setPaymentType] = useState(null);

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);
    const handleBack = () => navigate('/user-admin/payments');

    // Debounce function to prevent API calls on every keystroke
    const debounce = (func, delay) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), delay);
        };
    };

    const handleSearch = async (value) => {
        if (!value || value.length < 3) {
            setSearchOptions([]);
            return;
        }
        setSearchLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/admin/search-users?q=${value}`);
            const data = await response.json();
            if (response.ok && data.data) {
                const options = data.data.map(user => ({
                    value: user._id, // The unique MongoDB _id
                    label: `${user.name} (${user.idNumber}) - ${user.type}`,
                    // Attach the full user object to the option for easy access on select
                    ...user 
                }));
                setSearchOptions(options);
            }
        } catch (error) {
            console.error("Search error:", error);
            toast.error("Failed to search for users.");
            setSearchOptions([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const debouncedSearch = useCallback(debounce(handleSearch, 500), []);

    const onSelectUser = (value, option) => {
        // The full user object is attached to the 'option'
        setSelectedUser(option);
    };
    
    const handleSubmit = async (values) => {
        if (!selectedUser) {
            toast.error("Please search for and select a student or applicant first.");
            return;
        }
    
        setLoading(true);
        
        const adminName = localStorage.getItem('fullName') || 'Admin';

        let endpoint = '';
        let payload = {};
    
        const commonPayload = {
            paymentMethod: values.paymentMethod,
            amount: values.amount,
            referenceNumber: values.referenceNumber,
            paymentDate: values.paymentDate,
            notes: values.notes || '',
            adminName, // Who is performing the action
        };
    
        if (['Exam Fee Payment', 'Reservation Fee Payment'].includes(values.paymentType)) {
            endpoint = `${API_URL}/api/admin/manual-payment/miscellaneous`;
            payload = {
                ...commonPayload,
                applicantID: selectedUser.idNumber,
                email: selectedUser.email,
                applicantName: selectedUser.name,
                description: values.paymentType,
            };
        } else { // It's a tuition payment
            endpoint = `${API_URL}/api/admin/manual-payment/tuition`;
            payload = {
                ...commonPayload,
                studentID: selectedUser.idNumber,
                // Parse the payment type from the dropdown, e.g., "Tuition: Downpayment" -> "downpayment"
                paymentType: values.paymentType.split(':')[1].trim().toLowerCase().replace(' ', ''), 
            };
        }

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
    
            const data = await response.json();
    
            if (response.ok) {
                toast.success(data.message || 'Manual payment recorded successfully!');
                navigate('/user-admin/payments');
            } else {
                toast.error(data.message || 'Failed to record payment.');
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("A network error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={false} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack}>
                            <MdOutlineKeyboardArrowLeft />
                        </div>
                        <p className="heading">Create Payment</p>
                    </div>

                    <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={loading}>
                        <Card title={<><FaUserGraduate style={{marginRight: 8}}/>Step 1: Find Student / Applicant</>} style={{ marginBottom: 24, marginTop: '2%' }}>
                            <AutoComplete
                                style={{ width: '100%' }}
                                onSearch={debouncedSearch}
                                onSelect={onSelectUser}
                                placeholder="Search by Name or ID (min 3 characters)..."
                                options={searchOptions}
                                notFoundContent={searchLoading ? <Spin size="small" /> : 'No user found'}
                                allowClear
                                onClear={() => setSelectedUser(null)}
                            />
                            {selectedUser && (
                                <Card size="small" style={{ marginTop: 16, backgroundColor: '#fafafa' }}>
                                    <Text strong>{selectedUser.name}</Text>
                                    <br/>
                                    <Text type="secondary">ID: {selectedUser.idNumber} | Email: {selectedUser.email}</Text>
                                    <br/>
                                    <Text type="secondary">Level: {selectedUser.academicLevel || 'N/A'} ({selectedUser.academicStrand || 'N/A'})</Text>
                                </Card>
                            )}
                        </Card>

                        <Spin spinning={loading} tip="Submitting Payment...">
                            <Card title="Step 2: Enter Payment Details" style={{ opacity: selectedUser ? 1 : 0.5, pointerEvents: selectedUser ? 'auto' : 'none' }}>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="paymentType" label="Payment Type" rules={[{ required: true, message: 'Please select a payment type.' }]}>
                                            <Select placeholder="Select a payment type" onChange={setPaymentType}>
                                                <Option value="Exam Fee Payment">Exam Fee Payment</Option>
                                                <Option value="Reservation Fee Payment">Reservation Fee Payment</Option>
                                                <Option value="Tuition: Downpayment">Tuition: Downpayment</Option>
                                                <Option value="Tuition: Midterm Installment">Tuition: Midterm Installment</Option>
                                                <Option value="Tuition: Finals Installment">Tuition: Finals Installment</Option>
                                                <Option value="Tuition: Full Payment">Tuition: Full Payment</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="amount" label="Amount (PHP)" rules={[{ required: true, message: 'Please enter the amount.' }]}>
                                            <Input type="number" prefix="₱" placeholder="e.g., 500" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="paymentMethod" label="Payment Method" rules={[{ required: true, message: 'Please select a method.' }]}>
                                            <Select placeholder="Select a method">
                                                <Option value="cash">Cash (Over-the-Counter)</Option>
                                                <Option value="bank-transfer">Bank Transfer</Option>
                                                <Option value="check">Check</Option>
                                                <Option value="gcash-manual">GCash (Manual Entry)</Option>
                                                <Option value="maya-manual">Maya (Manual Entry)</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="paymentDate" label="Payment Date" rules={[{ required: true, message: 'Please select the payment date.' }]} initialValue={dayjs()}>
                                            <DatePicker style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={24}>
                                        <Form.Item name="referenceNumber" label="Official Receipt / Transaction No." rules={[{ required: true, message: 'A reference number is required.' }]}>
                                            <Input placeholder="Enter the OR number, bank transaction ID, etc." />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Divider />
                                <div className="buttons">
                                    <Button type="default" htmlType="button" onClick={handleBack}>
                                        Cancel
                                    </Button>
                                    <Button type="primary" htmlType="submit" className="create-btn">
                                        Record Payment
                                    </Button>
                                </div>
                            </Card>
                        </Spin>
                    </Form>
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default CreateManualPayment;